import { Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'taolo-theme';
const DEFAULT_THEME: Theme = 'dark';

/**
 * App theme (dark/light) management.
 *
 * Source of truth is the `theme` signal, persisted to localStorage and applied
 * as a `.dark` class on <html>. The same class is set pre-boot by an inline
 * script in index.html to avoid a flash; this service keeps the runtime in sync.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<Theme>(this.readStoredTheme());

  constructor() {
    // Reconcile the DOM with the stored value (the pre-boot script may have
    // run before storage was available, e.g. in private-mode edge cases).
    this.applyTheme(this.theme());
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
    this.applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Storage may be unavailable (private mode / blocked); theme still applies for the session.
    }
  }

  toggle(): void {
    this.setTheme(this.theme() === 'dark' ? 'light' : 'dark');
  }

  private readStoredTheme(): Theme {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : DEFAULT_THEME;
    } catch {
      return DEFAULT_THEME;
    }
  }

  private applyTheme(theme: Theme): void {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }
}
